<script setup lang="ts">
import type { PropType } from 'vue';
import type { SelectOption } from 'naive-ui';
import GaiaText from '../base/GaiaText.vue';
import GaiaSelect from '../base/selects/GaiaSelect.vue';
import { gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  /** The line under the label — say what each choice actually does. */
  hint: {
    type: String,
    default: undefined,
  },
  value: {
    type: [String, Number] as PropType<string | number | null>,
    default: null,
  },
  options: {
    type: Array as PropType<SelectOption[]>,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  /** plain = a row in a list of options; card = its own bordered surface. */
  card: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{ 'update:value': [value: string | number] }>();

const border = gaiaNeutral.borderCard;
const soft = gaiaNeutral.surfaceSoft;
const surface = gaiaNeutral.surface;
</script>

<template>
  <!-- GaiaToggleRow's sibling for a setting with more than two states (a
       role's org-wide access level): the same label and hint, a small select
       in place of the switch, so both sit in one list without the eye noticing
       the seam. Unlike the toggle row the row itself is not a click target —
       there is no single thing a click on it could mean. -->
  <div class="gaia-select-row" :class="{ card: props.card }">
    <n-flex vertical :size="2" :style="{ minWidth: 0 }">
      <gaia-text size="body" weight="semibold" tone="ink">{{ props.label }}</gaia-text>
      <gaia-text v-if="props.hint" size="control" tone="subtle" :style="{ lineHeight: 1.5 }">
        {{ props.hint }}
      </gaia-text>
    </n-flex>
    <gaia-select
      :value="props.value"
      :options="props.options"
      :disabled="props.disabled"
      size="small"
      :consistent-menu-width="false"
      :style="{ marginLeft: 'auto', flex: 'none', width: '140px' }"
      @update:value="(value: string | number) => emit('update:value', value)"
    />
  </div>
</template>

<style scoped>
/* Mirrors GaiaToggleRow's box so the two rows line up in one list. */
.gaia-select-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 11px 13px;
  border: 1px solid v-bind(border);
  border-radius: 10px;
  background: v-bind(surface);
}

.gaia-select-row.card {
  padding: 14px 16px;
  background: v-bind(soft);
}
</style>
